import CompletedOrProjectContainer from 'components/Project/CompletedOrProjectContainer'
import ProjectNotTranslated from 'components/Project/ProjectNotTranslated/ProjectNotTranslated'
import Loader from 'components/shared/Loader/Loader'
import NoMatch from 'components/shared/NoMatch/NoMatch'
import SiteHeader from 'components/shared/SiteHeader/SiteHeader'
import { projectComplete } from 'helpers/Project'
import {
  isKnowledgeQuiz,
  isReflection,
  projectHasBadge,
  stepPosition,
} from 'helpers/Step'
import { withRouter } from 'helpers/withRouter'
import i18next from 'i18next'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { withCookies } from 'react-cookie'
import { Helmet } from 'react-helmet'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import { generatePath } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { clearBadgeState, fetchIssuedBadge } from 'redux/actions/BadgeActions'
import { fetchFavourites } from 'redux/actions/FavouriteActions'
import {
  createProgress,
  fetchProjectProgress,
  updateProgress,
} from 'redux/actions/ProgressActions'
import { fetchProject, setProjectStep } from 'redux/actions/ProjectActions'
import { SENT_PROGRESS_OK } from 'redux/constants/ActionTypes'
import { NoPrint } from 'components/shared/NoPrint/NoPrint'

const changeProject = (dispatch, locale, slug, position = 0, branch) => {
  dispatch(clearBadgeState)
  dispatch(fetchProject({ locale, position, slug, branch }))
}

const getBadge = ({ project, authentication, session, locale }) => {
  if (project?.badgeTemplateId && project?.providerId) {
    return fetchIssuedBadge({
      locale,
      badge_template_id: project.badgeTemplateId,
      provider_id: project.providerId,
      user_id: authentication.user?.profile?.user || session,
    })
  }
  return null
}

const progressDispatch =
  ({
    dispatch,
    project,
    projectProgress,
    locale,
    slug,
    currentStepPosition,
    session,
    authentication,
    branch,
  }) =>
  (obtainBadge = true) => {
    const currentStep = project.steps[currentStepPosition]
    const completion = currentStep && currentStep.completion
    const token =
      authentication && authentication.user
        ? authentication.user.access_token
        : null
    const actions = {
      [SENT_PROGRESS_OK]: {
        action: getBadge({ project, authentication, session, locale }),
      },
    }
    if (!branch) {
      if (projectProgress.slug) {
        dispatch(
          updateProgress(
            locale,
            token,
            project.id,
            slug,
            currentStepPosition,
            completion,
            session,
            obtainBadge ? actions : {}
          )
        )
      } else {
        dispatch(
          createProgress(
            locale,
            token,
            project.id,
            slug,
            currentStepPosition,
            completion,
            session,
            obtainBadge ? actions : {}
          )
        )
      }
    }
  }

const ProjectContent = ({
  error,
  i18n,
  projectFetched,
  project,
  projectProgress,
  currentProjectSlug,
  progressCallback,
  projectLoading,
  router: { params, location },
}) => {
  if (projectFetched === -1) {
    return <NoMatch error={error} />
  } else if (projectFetched === 1 && project.availableLocales) {
    return <ProjectNotTranslated project={project} />
  } else if (
    (projectFetched === 1 || currentProjectSlug) &&
    projectProgress.loading === false &&
    projectLoading === false
  ) {
    const { slug } = params
    const { pathname } = location
    return (
      <React.Fragment>
        <Helmet>
          <link
            rel="canonical"
            href={`https://projects.raspberrypi.org/${i18n.language}/projects/${slug}`}
          />
        </Helmet>
        <CompletedOrProjectContainer
          projectComplete={projectComplete(pathname)}
          projectSlug={slug}
          progressCallback={progressCallback}
        />
      </React.Fragment>
    )
  }
  return <Loader />
}

const ProjectContainer = (props) => {
  const {
    authentication,
    badge,
    cookies,
    currentLocale,
    currentProjectSlug,
    currentStepPosition,
    dispatch,
    i18n,
    project,
    projectFetched,
    projectProgress,
    router: { navigate, params, location },
  } = props

  const searchParams = useSearchParams()[0]
  const branch = searchParams.get('branch')

  const [hasCurrentBadge, setHasCurrentBadge] = useState(false)

  const path = location.pathname
  const { slug, position } = params

  const token =
    authentication && authentication.user
      ? authentication.user.access_token
      : null
  const session = cookies.get('sessionProgress')
  const isLegacyComplete = projectComplete(path)

  useEffect(() => {
    if (project?.badgeTemplateId && badge?.badge?.id) {
      setHasCurrentBadge(project.badgeTemplateId === badge.badge.id)
    }
  }, [badge, project])

  useEffect(() => {
    if (projectFetched && !currentProjectSlug) {
      return // Project not available
    }

    if (currentProjectSlug === slug && currentLocale === i18n.language) {
      if (
        projectFetched &&
        project.badgeTemplateId &&
        project.providerId &&
        !hasCurrentBadge
      ) {
        dispatch(
          getBadge({ project, authentication, session, locale: i18n.language })
        )
      }
    }
  }, [
    dispatch,
    projectFetched,
    project,
    authentication,
    session,
    i18n.language,
    currentProjectSlug,
    slug,
    currentLocale,
    hasCurrentBadge,
  ])

  // Fetch favourites on login or locale change
  useEffect(() => {
    dispatch(fetchFavourites(i18n.language, token))
  }, [dispatch, i18n.language, token])

  // Fetch progress on project/locale/session/user change
  useEffect(() => {
    if (!branch) {
      dispatch(fetchProjectProgress(i18n.language, token, slug, session))
    }
  }, [dispatch, i18n.language, session, token, slug, branch])

  // Fetch project content if slug/locale changes
  useEffect(() => {
    if (projectFetched && !currentProjectSlug) {
      return // Project not available
    }

    if (currentProjectSlug !== slug || currentLocale !== i18n.language) {
      changeProject(dispatch, i18n.language, slug, position, branch)
    }
  }, [
    currentLocale,
    dispatch,
    i18n.language,
    position,
    projectFetched,
    currentProjectSlug,
    slug,
    branch,
  ])

  // Fetch project on language chooser change
  useEffect(() => {
    i18next.on('languageChanged', (newLocale) => {
      changeProject(dispatch, newLocale, slug, position, branch)
    })
    return () => {
      // remove existing handler when props change
      i18next.off('languageChanged')
    }
  }, [dispatch, slug, position, branch])

  // Set project position if saved in progress
  useEffect(() => {
    if (
      projectProgress.loading ||
      projectProgress.slug !== slug ||
      position ||
      isLegacyComplete
    ) {
      return
    }
    if (projectProgress.currentStep) {
      const newPath = generatePath('/:locale/projects/:slug/:position', {
        locale: i18n.language,
        slug,
        position: projectProgress.currentStep,
      })
      navigate(newPath)
    }
  }, [
    navigate,
    isLegacyComplete,
    i18n.language,
    path,
    position,
    projectProgress,
    slug,
  ])

  // Set project step if position changed
  useEffect(() => {
    if (isLegacyComplete) {
      return
    }
    if (projectFetched && currentStepPosition !== stepPosition(position)) {
      dispatch(setProjectStep(position))
    }
  }, [
    currentStepPosition,
    dispatch,
    isLegacyComplete,
    position,
    projectFetched,
  ])

  // Save progress if needed
  useEffect(() => {
    if (
      projectProgress.loading ||
      projectProgress.sending ||
      projectProgress.sent
    ) {
      return
    }

    const autoProgressExclusions =
      project.steps &&
      project.steps[currentStepPosition] &&
      (isKnowledgeQuiz(project.steps[currentStepPosition]) ||
        (isReflection(project.steps[currentStepPosition]) &&
          projectHasBadge(project)))

    if (
      projectFetched &&
      currentProjectSlug &&
      currentStepPosition === stepPosition(position) &&
      projectProgress.currentStep !== currentStepPosition &&
      !autoProgressExclusions
    ) {
      progressDispatch({
        dispatch,
        project,
        projectProgress,
        locale: i18n.language,
        slug,
        currentStepPosition,
        session,
        authentication,
        branch,
      })(false)
    }
  }, [
    currentStepPosition,
    dispatch,
    i18n.language,
    position,
    project,
    projectFetched,
    projectProgress,
    session,
    currentProjectSlug,
    slug,
    authentication,
    branch,
  ])

  const progressCallback = progressDispatch({
    dispatch,
    project,
    projectProgress,
    locale: i18n.language,
    slug,
    currentStepPosition,
    session,
    authentication,
    branch,
  })

  return (
    <div>
      <NoPrint>
        <SiteHeader locale={i18n.language} />
      </NoPrint>

      <ProjectContent {...{ progressCallback, ...props }} />
    </div>
  )
}

ProjectContainer.propTypes = {
  badge: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.object,
  router: PropTypes.shape({
    navigate: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    language: PropTypes.string.isRequired,
  }).isRequired,
  projectFetched: PropTypes.number.isRequired,
  currentProjectSlug: PropTypes.string,
  projects: PropTypes.object,
  project: PropTypes.object,
  favourites: PropTypes.object,
  progress: PropTypes.object,
  projectProgress: PropTypes.object,
  projectLoading: PropTypes.bool,
  authentication: PropTypes.object,
  currentLocale: PropTypes.string,
  currentStepPosition: PropTypes.number,
  cookies: PropTypes.object.isRequired,
}

ProjectContent.propTypes = {
  error: PropTypes.object,
  i18n: PropTypes.shape({
    language: PropTypes.string.isRequired,
  }).isRequired,
  currentProjectSlug: PropTypes.string,
  projectFetched: PropTypes.number.isRequired,
  project: PropTypes.object,
  projectProgress: PropTypes.object,
  projectLoading: PropTypes.bool,
  progressCallback: PropTypes.func.isRequired,
  router: PropTypes.shape({
    location: PropTypes.object.isRequired,
    params: PropTypes.object.isRequired,
  }).isRequired,
}

const mapStateToProps = (state) => {
  const {
    error,
    projectFetched,
    slug,
    project,
    currentLocale,
    currentStepPosition,
    loading: projectLoading,
  } = state.project
  const { authentication, badge, progress, projectProgress, favourites } = state
  return {
    badge,
    error,
    projectFetched,
    currentProjectSlug: slug,
    project,
    progress,
    projectProgress,
    projectLoading,
    favourites,
    authentication,
    currentLocale,
    currentStepPosition,
    projects: state.projects.entities,
  }
}

export { ProjectContainer }
export default connect(mapStateToProps)(
  translate('translations')(withCookies(withRouter(ProjectContainer)))
)
